import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { getReceiptById } from "../redux/actions/receiptActions";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import StatusChip from "../components/common/StatusChip";

import ReceiptDetailsTab from "../components/receipts/ReceiptDetailsTab";
import ReceiptPdfTab from "../components/receipts/ReceiptPdfTab";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

const ReceiptPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const { currentReceipt, loadingReceipt, errorLoadingReceipt } = useSelector(
    (state) => state.receipt,
  );

  const [activeTab, setActiveTab] = useState(0);
  const [receiptId, setReceiptId] = useState(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);
    if (match && match[0]) setReceiptId(match[0]);
  }, [location.pathname]);

  useEffect(() => {
    if (receiptId) dispatch(getReceiptById(receiptId));
  }, [dispatch, receiptId]);

  const handleBack = () => closeCurrentAndNavigate("/receipts");
  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  if (loadingReceipt) return <AppCircularProgress />;
  if (errorLoadingReceipt)
    return (
      <PageError
        error={errorLoadingReceipt}
        onClick={dispatch(getReceiptById(receiptId))}
      />
    );
  if (!currentReceipt) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <IconButton onClick={handleBack} color="inherit" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            sx={{ flexGrow: isMobile ? 1 : 0 }}
          >
            Receipt #{currentReceipt.transactionNumber}
          </Typography>
          {!isMobile && (
            <StatusChip status={currentReceipt.transactionStatus} />
          )}
        </Stack>

        {isMobile && (
          <Stack direction="row" spacing={1} mb={2} pl={5}>
            <StatusChip status={currentReceipt.transactionStatus} />
          </Stack>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued on {new Date(currentReceipt.receiptDateTime).toLocaleString()}{" "}
          • To: {currentReceipt.customerBusinessName}
        </Typography>

        {/* TABS */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Receipt Details" />
          <Tab label="PDF & Print" />
        </Tabs>
      </Box>

      {/* TAB CONTENT */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <ReceiptDetailsTab receipt={currentReceipt} />}
        {activeTab === 1 && <ReceiptPdfTab receipt={currentReceipt} />}
      </Box>
    </Box>
  );
};

export default ReceiptPage;
